import { DateTime } from 'luxon'
import type {
  Conversation,
  ConversationItem,
  ConversationItemAttachment,
  ConversationItemSentimentScore,
  ReplySentimentScore
} from '../messages.types'
import { ConversationItemType } from './messages.enums'
import type {
  IAttachment,
  IConversation,
  IConversationItem,
  IGlobalSearchResult
} from '@/models/interfaces'
import { GlobalSearchResultType } from '@/models/enums'

export function createDefaultConversationSearchResult(): IGlobalSearchResult {
  return {
    type: GlobalSearchResultType.Conversation,
    conversation: { itemID: 0 }
  }
}

export function isTargetedConversationSearchResult(
  searchResult: IGlobalSearchResult
): boolean {
  switch (searchResult.type) {
    case GlobalSearchResultType.Conversation:
      return !!searchResult.conversation?.itemID
    case GlobalSearchResultType.Message:
      return (
        !!searchResult.conversation?.itemID && !!searchResult.message?.itemID
      )
    case GlobalSearchResultType.Attachment:
      return (
        !!searchResult.conversation?.itemID && !!searchResult.attachment?.itemID
      )
    default:
      return false
  }
}

export function createConversation(
  overrides: Partial<Conversation> = {}
): Conversation {
  return {
    threadId: 0,
    lastReplyDate: DateTime.now(),
    hasCall: false,
    isNew: false,
    userId: 0,
    threadItems: [],
    lastReplyName: '',
    createDate: DateTime.now(),
    createdBy: '',
    lastReplyUserId: 0,
    ownerUserId: 0,
    ...overrides
  }
}

export function createConversationItem(
  overrides: Partial<ConversationItem> = {}
): ConversationItem {
  return {
    itemType: ConversationItemType.Reply,
    itemId: 0,
    entryDate: DateTime.now(),
    entryDateUtc: DateTime.now(),
    caseId: 0,
    threadId: 0,
    userId: 0,
    duration: 0,
    fileSize: 0,
    isBlobDeleted: false,
    ...overrides
  }
}

export function convertNewToOldConversation(
  conversation: Conversation
): IConversation {
  return {
    threadID: conversation.threadId,
    subject: conversation.subject,
    lastReplyDate: conversation.lastReplyDate,
    hasCall: conversation.hasCall,
    isNew: conversation.isNew,
    userID: conversation.userId,
    threadItems: conversation.threadItems?.map(convertNewToOldConversationItem),
    timeZoneIdentifier: conversation.timeZoneIdentifier,
    lastReplyName: conversation.lastReplyName,
    createDate: conversation.createDate,
    createdBy: conversation.createdBy,
    lastReplyUserID: conversation.lastReplyUserId,
    ownerUserID: conversation.ownerUserId,
    lastReplyPreview: conversation.lastReplyPreview,
    lastReplyFirstName: conversation.lastReplyFirstName,
    lastReplyLastName: conversation.lastReplyLastName,
    ownerFirstName: conversation.ownerFirstName,
    ownerLastName: conversation.ownerLastName
  }
}

export function convertNewToOldConversationItem(
  conversationItem: ConversationItem
): IConversationItem {
  return {
    itemType: conversationItem.itemType,
    itemID: conversationItem.itemId,
    message: conversationItem.message,
    entryDate: conversationItem.entryDate,
    entryDateUtc: conversationItem.entryDateUtc,
    altEntryDateUtc: conversationItem.altEntryDateUtc,
    caseID: conversationItem.caseId,
    threadID: conversationItem.threadId,
    userID: conversationItem.userId,
    name: conversationItem.name,
    fileName: conversationItem.fileName,
    initials: conversationItem.initials,
    duration: conversationItem.duration,
    attachments:
      conversationItem.itemType === ConversationItemType.Attachment
        ? [convertStandaloneThreadItemToAttachment(conversationItem)]
        : conversationItem.attachments?.map(convertNewToOldAttachment),
    isBlobDeleted: conversationItem.isBlobDeleted,
    referenceItemID: conversationItem.referenceItemId,
    sentimentScore: conversationItem.sentimentScore
      ? convertNewToOldSentimentScore(conversationItem.sentimentScore)
      : null
  }
}

function convertStandaloneThreadItemToAttachment(
  conversationItem: ConversationItem
): IAttachment {
  return {
    itemID: conversationItem.itemId,
    ownerID: conversationItem.userId,
    threadID: conversationItem.threadId,
    ...(conversationItem.referenceItemId != null && {
      replyID: conversationItem.referenceItemId
    }),
    ...(conversationItem.fileName && { fileName: conversationItem.fileName }),
    fileSize: conversationItem.fileSize,
    uploadDateTime: conversationItem.entryDate,
    uploadDateTimeUtc:
      conversationItem.altEntryDateUtc ?? conversationItem.entryDateUtc,
    isBlobDeleted: conversationItem.isBlobDeleted
  }
}

export function convertNewToOldAttachment(
  attachment: ConversationItemAttachment
): IAttachment {
  return {
    itemID: attachment.itemId,
    ownerID: attachment.ownerId,
    threadID: attachment.threadId,
    replyID: attachment.replyId,
    fileName: attachment.fileName,
    fileExtension: attachment.fileExtension,
    fileSize: attachment.fileSize,
    mimeType: attachment.mimeType,
    blobName: attachment.blobName,
    uploadDateTime: attachment.uploadDateTime,
    uploadDateTimeUtc: attachment.uploadDateTimeUtc,
    isBlobDeleted: attachment.isBlobDeleted,
    isArchived: attachment.isArchived
  }
}

export function convertNewToOldSentimentScore(
  sentimentScore: ConversationItemSentimentScore
): ReplySentimentScore {
  return {
    id: sentimentScore.id,
    messageId: sentimentScore.messageId,
    userId: sentimentScore.userId,
    score: sentimentScore.score,
    generatedByAction: sentimentScore.generatedByAction,
    createdWhen: sentimentScore.createdWhen,
    scanGuid: sentimentScore.scanGuid,
    userFeedback: sentimentScore.userFeedback ?? null,
    feedbackProvidedWhen: sentimentScore.feedbackProvidedWhen ?? null
  }
}
